import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";

import { useParams } from "react-router-dom";

import useAccount from "../../../../../hooks/useAccount";
import {
  useMutateCreateProjectViewLinkInvitation,
  useProjectViewLinkSharing,
} from "../../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../../hooks/useTracker";
import {
  useWebExport,
  WebExportAssignableUserRoles,
  WebExportAssignableUserRolesDisplayName,
} from "../../../../../hooks/useWebExport";
import { EmailInvitationForm, type EmailInvitationFormHandle } from "./EmailInvitationForm";

export const EmailInvitationFormViewLink = forwardRef<EmailInvitationFormHandle>((_, ref) => {
  const tracker = useTracker();
  const { data: webExportData } = useWebExport();
  const { viewLink = "" } = useParams();
  const invalidEmailList = useRef<string[]>([]);
  const emailFormRef = useRef<EmailInvitationFormHandle>(null);
  const {
    mutateAsync: mutateProjectViewLinkInvitation,
    isPending: isProjectViewLinkInvitationPending,
    isError: isProjectViewLinkInvitationError,
  } = useMutateCreateProjectViewLinkInvitation({
    onSuccess: (data) => {
      if (data.errors.length > 0) {
        invalidEmailList.current = data.errors.map((error) => error.email);
      } else {
        invalidEmailList.current = [];
        emailFormRef.current?.clearForm();
      }
    },
    onError: () => {
      invalidEmailList.current = [];
    },
  });

  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();
  const { data: accountData } = useAccount();
  const currentEmails = useMemo(() => {
    return [
      ...(projectViewLinkSharingData?.users.map((user) => user.email) ?? []),
      ...(accountData?.email ? [accountData.email] : []),
    ];
  }, [projectViewLinkSharingData, accountData]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      emailFormRef.current?.focus();
    },
    blur: () => {
      emailFormRef.current?.blur();
    },
    clearForm: () => {
      emailFormRef.current?.clearForm();
      invalidEmailList.current = [];
    },
  }));

  return (
    <EmailInvitationForm
      ref={emailFormRef}
      roles={WebExportAssignableUserRoles}
      roleDisplayName={WebExportAssignableUserRolesDisplayName}
      onSubmit={(data) => {
        return mutateProjectViewLinkInvitation(data);
      }}
      isPending={isProjectViewLinkInvitationPending}
      defaultRole="commenter"
      invalidEmailList={[...currentEmails, ...invalidEmailList.current]}
      isError={isProjectViewLinkInvitationError || invalidEmailList.current.length > 0}
      onEmailAdded={() => {
        tracker.trackShareModalEmailAdded({
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
          sharedResource: "review_link",
        });
      }}
      onInvalidEmailAdded={() => {
        tracker.trackShareModalInvalidEmailAdded({
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
          sharedResource: "review_link",
        });
      }}
      onRoleChanged={(newValue, oldValue) => {
        tracker.trackShareModalInviteRoleChanged({
          newValue: newValue,
          oldValue: oldValue,
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
          sharedResource: "review_link",
        });
      }}
      onEmailRemoved={() => {
        tracker.trackShareModalEmailRemoved({
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
          sharedResource: "review_link",
        });
      }}
      onInvitedPressed={(role) => {
        tracker.trackShareModalInvitePressed({
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
          role: role,
          sharedResource: "review_link",
        });
      }}
    />
  );
});
